import { useEffect, useRef } from 'react';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import {
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
  type AnimationPlaybackControls,
} from 'motion/react';
import { bumpRoutePending, TOP_PROGRESS, useRoutePending } from './top-progress-store';

const optedIn = (entry: { meta?: Record<string, unknown> }) => entry.meta?.[TOP_PROGRESS] === true;

const notOptedOut = (entry: { meta?: Record<string, unknown> }) =>
  entry.meta?.[TOP_PROGRESS] !== false;

/**
 * Mounted as a `Suspense` fallback for lazy-loaded routes. While mounted, it
 * increments the global "route pending" counter consumed by `TopProgressBar`.
 * Renders nothing visually — the bar is the visible affordance.
 */
export function RouteSuspenseTracker() {
  useEffect(() => {
    bumpRoutePending(1);
    return () => {
      bumpRoutePending(-1);
    };
  }, []);
  return null;
}

/**
 * Thin progress bar pinned to the top of the viewport. Visible whenever the
 * app has work the user is actively waiting on: a route Suspense boundary is
 * showing its fallback, a query tagged `TOP_PROGRESS` is doing its initial
 * load, or a mutation that hasn't opted out is running (see
 * `top-progress-store.ts` for the meta contract).
 *
 * Background refetches (e.g. `refetchInterval` polling or window-focus
 * refetches) are intentionally ignored even for tagged queries: those queries
 * already have data, so surfacing the bar for every poll tick would make it
 * flicker constantly. Only count fetches where the query has no data yet —
 * its first load.
 *
 * Visual: 3px gradient strip (primary → ring), with a soft glow shadow and a
 * brighter "comet head" highlight at the leading edge. Animation follows
 * NProgress-style keyframed trickle for a natural feel.
 */
export function TopProgressBar() {
  const isFetching = useIsFetching({
    predicate: (query) => query.state.data === undefined && optedIn(query),
  });
  const isMutating = useIsMutating({ predicate: (m) => notOptedOut(m) });
  const routePending = useRoutePending();
  const active = isFetching > 0 || isMutating > 0 || routePending > 0;
  const reduce = useReducedMotion();

  const scaleX = useMotionValue(0);
  const opacity = useMotionValue(0);
  const animationsRef = useRef<AnimationPlaybackControls[]>([]);

  useEffect(() => {
    for (const a of animationsRef.current) a.stop();
    animationsRef.current = [];

    if (active) {
      // If the previous cycle is still completing (scaleX near 1), reset so
      // the next cycle animates forward from 0 instead of backtracking.
      if (scaleX.get() >= 0.95) {
        scaleX.set(0);
        opacity.set(0);
      }
      animationsRef.current.push(
        animate(opacity, 1, { duration: reduce ? 0 : 0.18, ease: 'easeOut' }),
      );
      // Single monotonic animation from current value to ~0.95 with
      // front-loaded easing — fast initial ramp, slow trickle.
      animationsRef.current.push(
        animate(scaleX, 0.95, {
          duration: reduce ? 0 : 10,
          ease: [0.1, 0.85, 0.2, 1],
        }),
      );
    } else {
      animationsRef.current.push(
        animate(scaleX, 1, {
          duration: reduce ? 0 : 0.22,
          ease: [0.22, 1, 0.36, 1],
          onComplete: () => {
            const fade = animate(opacity, 0, {
              duration: reduce ? 0 : 0.28,
              ease: 'easeOut',
              onComplete: () => {
                scaleX.set(0);
              },
            });
            animationsRef.current.push(fade);
          },
        }),
      );
    }

    return () => {
      for (const a of animationsRef.current) a.stop();
    };
  }, [active, opacity, scaleX, reduce]);

  return (
    <motion.div
      data-testid="top-progress-bar"
      data-active={active || undefined}
      aria-hidden
      style={{ scaleX, opacity, transformOrigin: '0% 50%' }}
      className="pointer-events-none fixed inset-x-0 top-0 z-100 h-px bg-ring"
    >
      <motion.span
        aria-hidden
        animate={{ opacity: reduce ? 1 : [0.55, 1, 0.55] }}
        transition={{ duration: 2.2, ease: 'easeInOut', repeat: Infinity }}
        className="absolute inset-0 shadow-[0_0_4px_0_var(--color-ring),0_0_8px_0_var(--color-ring)]"
      />
    </motion.div>
  );
}
